import { useEffect, useState } from "react";
import "./UpdatePrompt.css";
import {
  getRunningBuild,
  getLatestBuild,
  clearStaleCaches,
  cleanupOldServiceWorker,
} from "../../utils/updateChecker";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Asks the user to update when a new deploy is detected.
 * Checks every 5 minutes and whenever the app returns to the foreground (important for iOS PWAs).
 * "Later" hides the prompt for that build; it returns for the next build or the next app launch.
 */
const UpdatePrompt = () => {
  const [latestBuild, setLatestBuild] = useState(null);
  const [dismissedBuild, setDismissedBuild] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;

    cleanupOldServiceWorker().catch(() => {});

    const runningBuild = getRunningBuild();
    if (!runningBuild) return undefined;

    const check = async () => {
      try {
        const latest = await getLatestBuild();
        if (latest && latest !== runningBuild) setLatestBuild(latest);
      } catch {
        /* offline or transient error — try again later */
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", check);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", check);
    };
  }, []);

  if (!latestBuild || dismissedBuild === latestBuild) return null;

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await clearStaleCaches();
    } finally {
      window.location.reload();
    }
  };

  return (
    <div
      className="update-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-title"
      aria-describedby="update-message"
    >
      <div className="update-box">
        <div className="update-icon">
          <span className="material-symbols-outlined">system_update</span>
        </div>
        <h3 id="update-title">Update available</h3>
        <p id="update-message">
          A new version of the app is ready. Update now to get the latest
          changes. Any unsaved changes on this page will be lost.
        </p>
        <div className="update-actions">
          <button
            type="button"
            className="update-btn update-btn--secondary"
            onClick={() => setDismissedBuild(latestBuild)}
            disabled={updating}
          >
            Later
          </button>
          <button
            type="button"
            className="update-btn update-btn--primary"
            onClick={handleUpdate}
            disabled={updating}
          >
            {updating ? "Updating…" : "Update now"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdatePrompt;
